import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { Button } from '~/eds/src/components/Button/Button';
import { EsignatureType } from '~/enums/EsignatureType';
import { api } from '~/redux/api/createApi';
import { selectTicketId } from '~/redux/selectors';

import { useHandleEsignAuthenticateAction } from './useHandleEsignAuthenticateAction';

export const ViewOnEsignPlatform = () => {
  const { toast } = useErrorToast();
  const intl = useIntl();
  const id = useSelector(selectTicketId);
  const {
    data: esignatureTokenStatus,
    isLoading: isLoadingTokenStatus,
    error: esignatureTokenStatusError,
  } = api.endpoints.getTokenStatus.useQuery(
    {
      ticketId: id,
    },
    {
      skip: !id,
    },
  );
  const [viewEnvelope, { isFetching }] =
    api.endpoints.viewEnvelope.useLazyQuery();
  const {
    handleEsignAuthenticateAction,
    isLoading: isLoadingEsignAuthenticate,
  } = useHandleEsignAuthenticateAction();
  const hasEsignatureIntegration =
    esignatureTokenStatus && esignatureTokenStatus.providerType;

  const handleViewEnvelope = async () => {
    const newTab = window.open('', '_blank');
    try {
      const result = await viewEnvelope({
        ticketId: id,
        // button is disabled if there is no integration so we know this will be defined
        vendor: esignatureTokenStatus?.providerType as EsignatureType,
        returnUrl: window.location.href,
      }).unwrap();
      if (result?.redirectUrl && newTab) {
        newTab.location.href = result.redirectUrl;
      }
    } catch (error) {
      if (newTab) {
        newTab.close();
      }

      if (error instanceof Error) {
        toast(error, { autoDismiss: false });
      }
    }
  };

  const getTooltip = () => {
    if (esignatureTokenStatusError) {
      return intl.formatMessage({
        id: 'ViewOnEsignPlatform.errorTooltip',
        defaultMessage:
          'There was a problem fetching your e-signature integration.',
        description:
          'Tooltip for the view on e-signature platform button when there is a problem fetching your e-signature integration.',
      });
    }

    if (!hasEsignatureIntegration) {
      return intl.formatMessage({
        id: 'ViewOnEsignPlatform.noEsignatureIntegrationTooltip',
        defaultMessage:
          'This account has not set up an e-signature provider yet.',
        description:
          'Tooltip for the view on e-signature platform button when there is no e-signature integration setup.',
      });
    }

    return undefined;
  };

  return (
    <Button
      text={intl.formatMessage({
        id: 'ViewOnEsignPlatform.viewInEsignPlatformButton',
        defaultMessage: 'View on E-sign Platform',
      })}
      onClick={() => handleEsignAuthenticateAction(handleViewEnvelope)}
      isLoading={
        isFetching || isLoadingTokenStatus || isLoadingEsignAuthenticate
      }
      disabled={Boolean(getTooltip())}
      tooltip={getTooltip()}
    />
  );
};
